/**
 * Work Types Section for Assistant Profile
 * Each work type has a checkbox and a rate range input
 */
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { HIREABLE_ROLES } from "@/lib/profileOptions";
import CheckBox from "./CheckBox";

export default function WorkTypesSection({ workTypes, setWorkTypes }) {
  // workTypes is an array of objects: [{type: "lead_photo", rate_min: 75, rate_max: 100}, ...]
  // Backward compat: might also be a flat string array from old data

  const isSelected = (roleValue) => {
    return workTypes.some(wt =>
      typeof wt === "string" ? wt === roleValue : wt.type === roleValue
    );
  };

  const getRate = (roleValue, field) => {
    const wt = workTypes.find(wt =>
      typeof wt === "object" && wt.type === roleValue
    );
    return wt ? (wt[field] || "") : "";
  };

  const toggleWorkType = (roleValue) => {
    if (isSelected(roleValue)) {
      setWorkTypes(workTypes.filter(wt =>
        typeof wt === "string" ? wt !== roleValue : wt.type !== roleValue
      ));
    } else {
      setWorkTypes([...workTypes, { type: roleValue, rate_min: "", rate_max: "" }]);
    }
  };

  const updateRate = (roleValue, field, value) => {
    setWorkTypes(workTypes.map(wt => {
      if (typeof wt === "string" && wt === roleValue) {
        return { type: roleValue, [field]: value };
      }
      if (typeof wt === "object" && wt.type === roleValue) {
        return { ...wt, [field]: value };
      }
      return wt;
    }));
  };

  return (
    <Card className="bg-white/[0.03] border-white/5 p-8 mb-6">
      <h2 className="text-xl font-semibold mb-2">Willing to Work As</h2>
      <p className="text-white/40 text-sm mb-6">Select the roles you're available for and set your rate range for each.</p>
      <div className="space-y-2">
        {HIREABLE_ROLES.map((role) => {
          const selected = isSelected(role.value);
          return (
            <div
              key={role.value}
              className={`p-4 bg-white/[0.05] border rounded-sm transition-all ${
                selected ? "border-white/40 bg-white/5" : "border-white/10"
              }`}
              data-testid={`work-type-${role.value}`}
            >
              <div className="flex items-center justify-between">
                <div
                  className="flex items-center gap-3 cursor-pointer flex-1"
                  onClick={() => toggleWorkType(role.value)}
                >
                  <CheckBox checked={selected} />
                  <span className="font-medium">{role.label}</span>
                </div>
                {selected && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-white/40">$</span>
                    <Input
                      type="number"
                      min="0"
                      value={getRate(role.value, "rate_min")}
                      onChange={(e) => updateRate(role.value, "rate_min", e.target.value)}
                      placeholder="Min"
                      className="w-20 h-8 bg-white/5 border-white/10 text-white text-center text-sm"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`rate-min-${role.value}`}
                    />
                    <span className="text-white/30">–</span>
                    <Input
                      type="number"
                      min="0"
                      value={getRate(role.value, "rate_max")}
                      onChange={(e) => updateRate(role.value, "rate_max", e.target.value)}
                      placeholder="Max"
                      className="w-20 h-8 bg-white/5 border-white/10 text-white text-center text-sm"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`rate-max-${role.value}`}
                    />
                    <span className="text-white/40">/hr</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
